import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { Chip, DataTable, StatusGlyph, toast } from "@outpave/ds";
import type { ChipTone, DataColumn, RowAction } from "@outpave/ds";
import { useMutation } from "@apollo/client";
import { Inline } from "components/layout/reusableLayouts";
import AddressStatus from "../../../enums/AddressStatus";
import { GET_ADDRESSES, UPDATE_ADDRESS } from "../../../graphql/address";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import { LocationFilterCriteriaProps } from "../../../pages/admin/Locations";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { HugeiconsIcon } from "@hugeicons/react";
import { StarIcon } from "@hugeicons-pro/core-solid-standard";
import {
  Edit01Icon,
  LocationAdd02Icon,
  LocationOffline01Icon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { getStateFullName } from "utils/addressFormat";

interface AddressData {
  id: string;
  addressName: string;
  status: string;
  state: string;
  city: string;
  defaultLocation?: boolean;
}

interface LocationsTableProps {
  handleOpenLocationsDrawer: (id: string, editMode?: boolean) => void;
  refreshLocations: number;
  filterCriteria: LocationFilterCriteriaProps;
}

const LocationsTable: React.FC<LocationsTableProps> = (props) => {
  const { handleOpenLocationsDrawer, refreshLocations, filterCriteria } = props;
  const { t } = useTranslation();

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status: filterCriteria.status,
        searchTerm: filterCriteria.searchTerm,
      });
    }
  }, [filterCriteria]);

  const extractAddressData = (data: any): AddressData[] => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((edge: any) => ({
      id: edge.node.id,
      addressName: edge.node.addressName,
      status: edge.node.status,
      state: getStateFullName(edge.node.state),
      city: edge.node.city,
      defaultLocation: edge.node?.defaultLocation,
    }));
  };

  const filterAddressData = (item: any) => {
    return item.status === filterCriteria.status;
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_ADDRESSES,
      initialVariables: defaultPaginationVariables,
      extractData: extractAddressData,
      filterFunction: filterAddressData,
      refetchIndex: refreshLocations,
    });

  const [updateLocation, { loading: updatingLocation }] = useMutation(
    UPDATE_ADDRESS,
    {
      onCompleted: () => {
        toast.success(t("Location updated successfully"));
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  const setLocationStatus = (location: AddressData, status: AddressStatus) => {
    updateLocation({
      variables: {
        id: location.id,
        input: {
          addressName: location.addressName,
          status,
        },
      },
    });
  };

  // Default location pins to the top regardless of the server's ordering.
  const rows = useMemo(
    () =>
      dataArray
        .slice()
        .sort(
          (a: AddressData, b: AddressData) =>
            (b.defaultLocation === true ? 1 : 0) -
            (a.defaultLocation === true ? 1 : 0),
        ),
    [dataArray],
  );

  const columns: DataColumn<AddressData>[] = useMemo(
    () => [
      {
        id: "addressName",
        header: t("Location Name"),
        cell: (location) => location.addressName,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (location) => {
          const isActive = location.status === AddressStatus.ACTIVE;
          // StatusGlyph resolves off the untranslated name, so the glyph stays
          // right in every locale while the label renders translated.
          const name = isActive ? "Active" : "Closed";
          const tone: ChipTone = isActive ? "positive" : "warning";

          return (
            <Inline gap="var(--space-3)" wrap={false}>
              <Chip
                tone={tone}
                size="sm"
                leading={<StatusGlyph status={name} tone={tone} />}
              >
                {t(name)}
              </Chip>
              {/* The chip's leading glyph carries the status, so the star sits
                  beside the chip rather than in that slot. */}
              {location.defaultLocation === true && (
                <HugeiconsIcon
                  icon={StarIcon}
                  size={16}
                  color="var(--color-text-tertiary)"
                  aria-label={t("Default")}
                />
              )}
            </Inline>
          );
        },
      },
      {
        id: "state",
        header: t("State"),
        cell: (location) => location.state,
      },
      {
        id: "city",
        header: t("City"),
        cell: (location) => location.city,
      },
    ],
    [t],
  );

  const rowActions = (location: AddressData): RowAction[] => [
    {
      label: t("View Location Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={16} />,
      onClick: () => handleOpenLocationsDrawer(location.id),
    },
    {
      label: t("Edit Location Details"),
      icon: <HugeiconsIcon icon={Edit01Icon} size={16} />,
      onClick: () => handleOpenLocationsDrawer(location.id, true),
    },
    ...(location.defaultLocation === true ||
    location.status === AddressStatus.CLOSED
      ? []
      : [
          {
            label: t("Close Location"),
            icon: <HugeiconsIcon icon={LocationOffline01Icon} size={16} />,
            onClick: () => setLocationStatus(location, AddressStatus.CLOSED),
          },
        ]),
    ...(location.status === AddressStatus.ACTIVE
      ? []
      : [
          {
            label: t("Open Location"),
            icon: <HugeiconsIcon icon={LocationAdd02Icon} size={16} />,
            onClick: () => setLocationStatus(location, AddressStatus.ACTIVE),
          },
        ]),
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <DataTable<AddressData>
        columns={columns}
        rows={rows}
        getRowKey={(location) => location.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(location) => handleOpenLocationsDrawer(location.id)}
        onScroll={handleScroll}
        emptyState={t("No locations found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more locations")}
        stickyHeader
      />
      <LoadingWithoutModal open={updatingLocation} />
    </Box>
  );
};

export default LocationsTable;
